(() => {
  const routes = window.routes || {
    cart_change_url: `${(window.Shopify && window.Shopify.routes?.root) || '/'}cart/change.js`,
  };

  const fetchConfig = () => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  });

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  // ---------- Quantity helpers ----------
  function clampInt(v, min = 1) {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? min : Math.max(min, n);
  }

  function syncMinusState(inputEl) {
    const minusBtn = inputEl.closest('.qty-input')?.querySelector('.qty-count--minus');
    if (minusBtn) minusBtn.disabled = clampInt(inputEl.value) <= 1;
  }

  function initQuantityInputs() {
    document.querySelectorAll('.product-qty').forEach((input) => {
      if (input.dataset.enhancedQtyInput === '1') return;
      input.dataset.enhancedQtyInput = '1';

      const normalize = () => {
        input.value = String(clampInt(input.value));
        syncMinusState(input);
      };

      input.addEventListener('input', normalize);
      input.addEventListener('change', normalize);
      normalize(); // init state
    });
  }

  function initQuantityButtons() {
    document.querySelectorAll('.qty-count').forEach((btn) => {
      if (btn.dataset.enhancedQtyBtn === '1') return;
      btn.dataset.enhancedQtyBtn = '1';

      btn.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopImmediatePropagation();
          e.stopPropagation();

          const input = btn.closest('.qty-input')?.querySelector('.product-qty');
          if (!input) return;

          const current = clampInt(input.value);
          const next = btn.dataset.action === 'add' ? current + 1 : Math.max(1, current - 1);

          input.value = String(next);
          syncMinusState(input);

          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        },
        { passive: false }
      );
    });
  }

  // Helper to find a reasonable quantity near a reference element (falls back to global)
  function getScopedQuantity(refEl) {
    const candidates = [
      refEl?.closest('.qty-input')?.querySelector('.product-qty'),
      refEl?.closest('.product-header-actions')?.querySelector('.product-qty'),
      document.querySelector('.product-header-actions .product-qty'),
      document.querySelector('.sticky-atc-bar .product-qty'),
    ].filter(Boolean);

    for (const el of candidates) {
      const val = clampInt(el.value);
      if (val > 0) return val;
    }
    return 1;
  }

  // ---------- Cart notification ----------
  function ensureNotificationUI() {
    const headers = document.querySelectorAll('header');
    headers.forEach((header, i) => {
      if (!header.querySelector(`#cart-notification-${i}`)) {
        const wrap = document.createElement('div');
        wrap.innerHTML = `
          <div id="cart-notification-${i}" class="cart-notification">
            <div class="cart-notification__content"></div>
          </div>
        `;
        header.appendChild(wrap.firstElementChild);
      }
    });
  }

  class CartNotification {
    constructor() {
      ensureNotificationUI();
      this.els = [...document.querySelectorAll('.cart-notification')];
      this.hideTimers = [];
      document.addEventListener('click', (e) => {
        this.els.forEach((el, i) => { if (!el.contains(e.target)) this.hide(i); });
      });
    }

    show(items) {
      this.els.forEach((el, i) => {
        const content = el.querySelector('.cart-notification__content');
        if (!content) return;

        content.innerHTML = '';

        items.forEach((item) => {
          const wrap = document.createElement('div');
          wrap.className = 'cart-notification__line';
          wrap.innerHTML = `
            <img class="cart-notification__image" src="${item.image || ''}" alt="${item.title}" />
            <div class="cart-notification__text">
              <p class="cart-notification__title">${item.title}</p>
              <p class="cart-notification__qty">Quantity: ${item.quantity || 1}</p>
            </div>
          `;
          content.appendChild(wrap);
        });

        el.classList.add('show');
        if (this.hideTimers[i]) clearTimeout(this.hideTimers[i]);
        this.hideTimers[i] = setTimeout(() => this.hide(i), 3000);
      });
    }

    hide(index) {
      if (typeof index === 'number') this.els[index].classList.remove('show');
      else this.els.forEach((el) => el.classList.remove('show'));
    }
  }
  window.cartNotification = new CartNotification();

  // ---------- Header count updates ----------
  function updateCartCountsFromHeaderSection(headerHTML) {
    if (!headerHTML) return;
    const dom = parseHTML(headerHTML);
    const newCountEl = dom.querySelector('.cart-icon_item-count');
    if (newCountEl) {
      document.querySelectorAll('.cart-icon_item-count').forEach((el) => {
        el.innerHTML = newCountEl.innerHTML;
      });
    }
    const newB2B = dom.getElementById('b2b-cart-count');
    if (newB2B && document.getElementById('b2b-cart-count')) {
      document.getElementById('b2b-cart-count').innerHTML = newB2B.innerHTML;
    }
  }

  async function refreshCountsFallback() {
    try {
      const res = await fetch(`${(window.Shopify && window.Shopify.routes?.root) || '/'}cart.js`);
      const cart = await res.json();
      const count = cart.item_count || 0;
      document.querySelectorAll('.cart-icon_item-count, #b2b-cart-count').forEach((el) => {
        el.textContent = count;
      });
    } catch (e) {
      console.warn('Could not refresh counts via cart.js', e);
    }
  }

  // ---------- Add-to-cart custom element ----------
  class AddToCartBtn extends HTMLElement {
    static get observedAttributes() {
      return ['data-productid'];
    }

    constructor() {
      super();
      this.defaultID = this.getAttribute('data-productid') || this.dataset.productid || '';
      this.originalHTML = this.innerHTML || 'Add to Cart';
      this.addEventListener('click', this.addProduct.bind(this));
    }

    attributeChangedCallback(name, _oldValue, newValue) {
      if (name === 'data-productid' && newValue && newValue !== this.defaultID) {
        this.defaultID = newValue;
      }
    }

    setSoldOutUI() {
      this.classList.add('add-to-cart--soldout');
      this.disabled = true;
      this.innerHTML = 'Sold Out';

      // Hide qty input in the *nearest* container (works for static + sticky)
      const qtyInput = this.closest('.product-header-actions, .sticky-atc-bar')?.querySelector('.qty-input');
      if (qtyInput) qtyInput.style.setProperty('display', 'none', 'important');

      // Insert Notify Me (if missing) next to this specific button
      if (!this.nextElementSibling || !this.nextElementSibling.classList.contains('klaviyo-bis-trigger')) {
        const notifyBtn = document.createElement('a');
        notifyBtn.className = 'btn klaviyo-bis-trigger';
        notifyBtn.href = '#';
        notifyBtn.textContent = 'Notify Me When Available';
        this.insertAdjacentElement('afterend', notifyBtn);
      }
    }

    setAvailableUI() {
      this.classList.remove('add-to-cart--soldout');
      this.disabled = false;
      this.innerHTML = this.originalHTML;

      // Restore qty input visibility for the *nearest* container (static + sticky)
      const qtyInput = this.closest('.product-header-actions, .sticky-atc-bar')?.querySelector('.qty-input');
      if (qtyInput) qtyInput.style.setProperty('display', '', 'important');

      // Remove Notify Me (if present) next to this specific button
      if (this.nextElementSibling && this.nextElementSibling.classList.contains('klaviyo-bis-trigger')) {
        this.nextElementSibling.remove();
      }
    }

    setLoading(isLoading) {
      if (isLoading) {
        this.classList.add('loading');
        this.disabled = true;
        this.innerHTML = `<span class="spinner"></span> Adding...`;
      } else {
        this.classList.remove('loading');
        this.disabled = false;
        this.innerHTML = this.originalHTML;
      }
    }

    addProduct(event) {
      event.preventDefault();
      if (this.classList.contains('add-to-cart--soldout')) return;
      this.setLoading(true);

      let quantity = 1;
      const container = this.closest('.product-header-actions') || this.closest('.sticky-atc-bar');
      const qtyInput = container ? container.querySelector('.product-qty') : null;
      if (qtyInput && qtyInput.value) quantity = clampInt(qtyInput.value);

      const attrId = this.getAttribute('data-productid') || this.dataset.productid;
      const select = document.querySelector('.product-select');
      const idInput = document.querySelector('input[name="id"]');

      let variantID =
        (attrId && String(attrId)) ||
        (idInput && idInput.value) ||
        (select && select.value) ||
        this.defaultID;

      variantID = String(parseInt(variantID, 10));

      const isB2B = !!document.querySelector('.b2b-nav-top-menu');
      const body = JSON.stringify({
        items: [{ id: parseInt(variantID, 10), quantity }],
        sections: isB2B ? ['header'] : ['header'],
        sections_url: window.location.pathname,
      });

      fetch(`${(window.Shopify && window.Shopify.routes?.root) || '/'}cart/add.js`, { ...fetchConfig(), body })
        .then((r) => r.json())
        .then((data) => {
          const items = (data && data.items) || [];
          const mapped = items.map((it) => ({
            title: it.title,
            image: it.image || (it.featured_image && it.featured_image.url) || '',
            quantity: it.quantity || 1,
          }));
          window.cartNotification.show(mapped);
          if (data?.sections?.header) {
            updateCartCountsFromHeaderSection(data.sections.header);
          } else {
            refreshCountsFallback();
          }
        })
        .catch((err) => {
          console.error('Error adding to cart:', err);
          refreshCountsFallback();
        })
        .finally(() => {
          this.setLoading(false);
        });
    }
  }

  if (!customElements.get('add-to-cart-btn')) {
    customElements.define('add-to-cart-btn', AddToCartBtn);
  }

  // ---------- Direct-add buttons ----------
  function initDirectAddButtons() {
    document.querySelectorAll('button.direct-add').forEach((btn) => {
      if (btn.dataset.enhanced === '1') return;
      btn.dataset.enhanced = '1';

      const originalHTML = btn.innerHTML;
      const setLoading = (isLoading) => {
        if (isLoading) {
          btn.classList.add('loading');
          btn.disabled = true;
          btn.innerHTML = `<span class="spinner"></span> Adding...`;
        } else {
          btn.classList.remove('loading');
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }
      };

      btn.addEventListener('click', (e) => {
        e.preventDefault();

        let quantity = 1;
        const container = btn.closest('.product-header-actions');
        const qtyInput = container ? container.querySelector('.product-qty') : null;
        if (qtyInput && qtyInput.value) quantity = clampInt(qtyInput.value);

        const variantID = btn.dataset.id;
        setLoading(true);

        const isB2B = !!document.querySelector('.b2b-nav-top-menu');
        const body = JSON.stringify({
          items: [{ id: parseInt(variantID, 10), quantity }],
          sections: isB2B ? ['header'] : ['header'],
          sections_url: window.location.pathname,
        });

        fetch(`${(window.Shopify && window.Shopify.routes?.root) || '/'}cart/add.js`, { ...fetchConfig(), body })
          .then((r) => r.json())
          .then((data) => {
            const items = (data && data.items) || [];
            const mapped = items.map((it) => ({
              title: it.title,
              image: it.image || (it.featured_image && it.featured_image.url) || '',
              quantity: it.quantity || 1,
            }));
            window.cartNotification.show(mapped);
            if (data?.sections?.header) {
              updateCartCountsFromHeaderSection(data.sections.header);
            } else {
              refreshCountsFallback();
            }
          })
          .catch((err) => {
            console.error('Error adding to cart:', err);
            refreshCountsFallback();
          })
          .finally(() => {
            setLoading(false);
          });
      });
    });
  }

  // ---------- Upsell ATC ----------
  function initUpsellATC() {
    document.querySelectorAll('.product-upsells__atc-new').forEach((btn) => {
      if (btn.dataset.enhanced === '1') return;
      btn.dataset.enhanced = '1';

      const originalHTML = btn.innerHTML;
      const setLoading = (isLoading) => {
        if (isLoading) {
          btn.classList.add('loading');
          btn.disabled = true;
          btn.innerHTML = `<span class="spinner"></span> Adding...`;
        } else {
          btn.classList.remove('loading');
          btn.disabled = false;
          btn.innerHTML = originalHTML;
        }
      };

      btn.addEventListener('click', async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
          const currentVariantId = getCurrentVariantIdFromDOM();
          if (!currentVariantId) throw new Error('No current variant found.');
          const currentQty = getScopedQuantity(btn);

          let upsellVariants = [];
          try {
            upsellVariants = JSON.parse(btn.dataset.upsells || '[]');
          } catch {
            upsellVariants = [];
          }

          const items = [
            { id: parseInt(currentVariantId, 10), quantity: clampInt(currentQty) },
            ...upsellVariants.map((id) => ({ id: parseInt(id, 10), quantity: 1 }))
          ].filter((it) => !isNaN(it.id));

          if (!items.length) throw new Error('No valid items to add.');

          const isB2B = !!document.querySelector('.b2b-nav-top-menu');
          const body = JSON.stringify({
            items,
            sections: isB2B ? ['header'] : ['header'],
            sections_url: window.location.pathname,
          });

          const res = await fetch(`${(window.Shopify && window.Shopify.routes?.root) || '/'}cart/add.js`, { ...fetchConfig(), body });
          const data = await res.json();

          const mapped = (data?.items || []).map((it) => ({
            title: it.title,
            image: it.image || (it.featured_image && it.featured_image.url) || '',
            quantity: it.quantity || 1,
          }));
          window.cartNotification.show(mapped);

          if (data?.sections?.header) {
            updateCartCountsFromHeaderSection(data.sections.header);
          } else {
            refreshCountsFallback();
          }
        } catch (err) {
          console.error('Error adding upsell to cart:', err);
          refreshCountsFallback();
        } finally {
          setLoading(false);
        }
      });
    });
  }

  // ---------- Product / Variant utilities ----------
  function getCurrentVariantIdFromDOM() {
    const idInput = document.querySelector('input[name="id"]');
    if (idInput && idInput.value) return idInput.value;
    const selectId = document.querySelector('select[name="id"]');
    if (selectId && selectId.value) return selectId.value;
    const customSelect = document.querySelector('.product-select');
    if (customSelect && customSelect.value) return customSelect.value;
    const urlVar = new URLSearchParams(window.location.search).get('variant');
    if (urlVar) return urlVar;
    const atc = document.querySelector('add-to-cart-btn');
    if (atc && (atc.getAttribute('data-productid') || atc.dataset.productid)) {
      return atc.getAttribute('data-productid') || atc.dataset.productid;
    }
    return null;
  }

  function findVariantById(productData, variantId) {
    if (!productData || !productData.variants) return null;
    return productData.variants.find((v) => String(v.id) === String(variantId)) || null;
  }

  function findVariantByOptions(productData) {
    if (!productData || !productData.options || !productData.variants) return null;
    const selected = [];
    productData.options.forEach((opt, idx) => {
      const pos = idx + 1;
      const sel =
        document.querySelector(`[name="options[${pos}]"]`) ||
        document.querySelector(`[name="options[${opt.name}]"]`) ||
        document.querySelector(`select[name="option${pos}"]`);
      if (!sel) return;
      if (sel.tagName === 'SELECT') {
        selected.push(sel.value);
      } else {
        const checked = document.querySelector(`[name="${sel.name}"]:checked`);
        if (checked) selected.push(checked.value);
      }
    });
    if (!selected.length) return null;

    return (
      productData.variants.find(
        (v) =>
          (v.option1 || null) === (selected[0] || null) &&
          (v.option2 || null) === (selected[1] || null) &&
          (v.option3 || null) === (selected[2] || null)
      ) || null
    );
  }

  // ✅ Update ALL add-to-cart buttons (static + sticky) to keep them perfectly in sync
  function syncATCWithVariant(variant) {
    const btns = document.querySelectorAll('add-to-cart-btn');
    if (!btns.length || !variant) return;
    btns.forEach((btn) => {
      btn.setAttribute('data-productid', String(variant.id));
      btn.dataset.productid = String(variant.id);
      if (variant.available) {
        btn.setAvailableUI();
      } else {
        btn.setSoldOutUI();
      }
    });
  }

  function initVariantWatchers(productData) {
    const recompute = () => {
      const byId = getCurrentVariantIdFromDOM();
      let variant = byId ? findVariantById(productData, byId) : null;
      if (!variant) variant = findVariantByOptions(productData);
      if (!variant) variant = productData.variants && productData.variants[0];
      syncATCWithVariant(variant);
    };

    recompute();

    const selectors = [
      'select[name="id"]',
      '.product-select',
      'input[name="id"]',
      '[name^="options["]',
      'select[name^="option"]',
    ];
    selectors.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => {
        el.addEventListener('change', recompute);
        el.addEventListener('input', recompute);
      });
    });

    window.addEventListener('popstate', recompute);

    const form = document.querySelector('form[action^="/cart/add"]') || document.querySelector('form.product-form');
    if (form && window.MutationObserver) {
      const mo = new MutationObserver(() => recompute());
      mo.observe(form, { subtree: true, attributes: true, childList: true });
    }
  }

  // ---------- Sticky ATC Watcher ----------
  function initStickyATCWatcher() {
    const staticATC = document.querySelector('.product-header-actions');
    const stickyATC = document.querySelector('.sticky-atc-bar');
    if (!staticATC || !stickyATC) return;

    const toggleSticky = () => {
      const rect = staticATC.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        stickyATC.style.display = 'none';
      } else {
        stickyATC.style.display = 'block';
      }
    };

    toggleSticky();
    document.addEventListener('scroll', toggleSticky);
    window.addEventListener('resize', toggleSticky);
  }

  // ---------- Bootstrap ----------
  async function init() {
    initQuantityInputs();
    initQuantityButtons();
    initDirectAddButtons();
    initUpsellATC();

    try {
      const handle = (window.location.pathname.match(/\/products\/([^\/?#]+)/) || [])[1];
      if (handle) {
        const res = await fetch(`/products/${handle}.js`);
        if (res.ok) {
          const productData = await res.json();
          if (productData?.variants?.length) {
            initVariantWatchers(productData);
          }
        }
      }
    } catch (e) {
      console.warn('Product data unavailable for variant watchers.', e);
    }

    initStickyATCWatcher();
  }

  document.addEventListener('DOMContentLoaded', init);
})();
